// src/Consultation.js
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";
import { useI18n } from "../i18n/I18nContext";

import {
  TrackingFields,
  TrackingDateEnCoursInput,
  TrackingDateFiniInput,
  TrackingPlacageRestantInput,
  TrackingSaveButton,
  TrackingStatutSelect,
} from "./TrackingEditCells";

const DEFAULT_BOIS_FOURNISSEUR = "Arbor Group";

export default function Consultation() {
  const { t, ui } = useI18n();

  const navigate = useNavigate();
  const location = useLocation();
  const isSuiviBois = location.pathname.includes("/logistique/stock/bois");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [recalculating, setRecalculating] = useState(false);
  const [recalcMsg, setRecalcMsg] = useState("");

  // --- Contrôles tri / filtre ---
  // Suivi bois : par défaut Arbor Group + placages restants > 0
  const [sortKey, setSortKey] = useState("index");
  const [sortDir, setSortDir] = useState("asc");
  const [filterKey, setFilterKey] = useState(
    isSuiviBois ? "fournisseur" : "",
  );
  const [filterValue, setFilterValue] = useState(
    isSuiviBois ? DEFAULT_BOIS_FOURNISSEUR : "",
  );
  const [filterKey2, setFilterKey2] = useState("");
  const [filterValue2, setFilterValue2] = useState("");
  const [onlyRestantPositif, setOnlyRestantPositif] = useState(isSuiviBois);

  // --- Pagination ---
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

    // ✅ NOUVEAU : Sélection multiple pour impression batch
  const [selectedPNs, setSelectedPNs] = useState(new Set());
  const [printing, setPrinting] = useState(false);
  const [pdfBusyPn, setPdfBusyPn] = useState("");


  function applyFilter(arr, key, value) {
    if (!key || String(value).trim() === "") return arr;
    const needle = String(value).toLowerCase();
    return arr.filter((r) => {
      const v = r[key];
      if (v == null) return false;
      return String(v).toLowerCase().includes(needle);
    });
  }

  // --- utils CSV ---
  const rmAccents = (s) =>
    String(s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  function asFrNumber(v) {
    if (typeof v === "number" && Number.isFinite(v)) {
      return v.toLocaleString("fr-FR", { useGrouping: false });
    }
    if (typeof v === "string") {
      const n = Number(v.replace(",", "."));
      if (Number.isFinite(n)) {
        return n.toLocaleString("fr-FR", { useGrouping: false });
      }
    }
    return v;
  }

  function toCsvValue(v) {
    if (v == null) return "";
    const s = String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }

  async function parseJsonResponse(res) {
    const text = await res.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch {
      const hint = text.trimStart().startsWith("<!")
        ? "Le serveur a renvoyé une page HTML au lieu de JSON. Vérifiez que Flask tourne sur le port 5000 et redémarrez-le."
        : "Réponse serveur invalide.";
      throw new Error(hint);
    }
  }

  function handleExportCsv(viewRows) {
    const headers = [
      "INDEX",
      "NUM LOT WOODOO",
      "FOURNISSEUR",
      "ETAT",
      "DATE DE RÉCEPTION",
      "DATE EN COURS",
      "DATE FINI",
      "QUANTITÉ NOK",
      "NB PLACAGE",
      "NB PLACAGE RESTANT",
      "PRIX TOTALES (€)",
    ].map(rmAccents);

    const data = viewRows.map((r) => [
      r.index,
      r.numLot || "",
      r.fournisseur || "",
      r.etat || "",
      r.dateReception || "",
      r.dateEnCours || "",
      r.dateFini || "",
      asFrNumber(r.quantiteKO),
      asFrNumber(r.nombreDePlacage),
      asFrNumber(r.nombrePlacageRestant),
      asFrNumber(r.prixTotale),
    ]);

    const csvBody = [headers, ...data]
      .map((row) => row.map(toCsvValue).join(";"))
      .join("\n");

    const csv = `sep=;\n${csvBody}`;
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });

    const d = new Date();
    const ts = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stock_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 100);
  }

  // ✅ NOUVEAU : Options pour construire les détails QR
  const [options, setOptions] = useState({
    essences: [],
    couleurs: [],
    coupes: [],
  });


  /* ===== Calcul prix total bois ===== */
function calcPrixTotal(it) {
  const prixM3          = toNumber(it.PrixMetreCube);
  const nbPlacageRestant = toNumber(it.NombreDePlacageRestant);

  if (!prixM3 || !nbPlacageRestant) return "";

  return Math.round((nbPlacageRestant / 150) * prixM3 * 100) / 100;
}



function mapBoisRows(data) {
  return (data || []).map((it, i) => {
    const qKO = toNumber(it.PliesNOK ?? 0) || 0;
    return {
      index: i + 1,
      numLot: it.PN,
      etat: it.Statut,
      fournisseur: it.Fournisseur,
      dateReception: formatDate(it?.Date),
      dateEnCours: formatDate(it?.Date_en_cours),
      dateFini: formatDate(it?.Date_fini),
      quantiteKO: qKO,
      nombreDePlacage: toNumber(it.NombreDePlacage),
      nombrePlacageRestant: toNumber(it.NombreDePlacageRestant),
      quantiteRestante: toNumber(it.NombreDePlacageRestant),
      prixTotale: calcPrixTotal(it),
      certif: it.Certif || "",
      certifPdfKey: it.CertifPdfKey || "",
      certifPdfName: it.CertifPdfName || "",
      raw: it,
    };
  });
}

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await apiFetch("/api/list");
        if (!r.ok) throw new Error("Erreur serveur");
        const data = await r.json();
        if (!cancelled) setRows(mapBoisRows(data));
      } catch (e) {
        if (!cancelled) setErr(e.message || "Erreur");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleRecalcStock() {
    if (!window.confirm(
      "Recalculer quantités restantes, NOK, dates et statuts depuis l'historique des prélèvements ?"
    )) return;
    setRecalculating(true);
    setRecalcMsg("");
    setErr("");
    try {
      const r = await apiFetch("/api/bois/recalc-stock", { method: "POST" });
      const data = await parseJsonResponse(r);
      if (!r.ok || data.status !== "success") {
        throw new Error(data.message || "Erreur recalcul");
      }
      setRecalcMsg(data.message || "Stock recalculé.");
      setLoading(true);
      const listRes = await apiFetch("/api/list");
      const listData = await listRes.json();
      setRows(mapBoisRows(listData));
    } catch (e) {
      setErr(e.message || "Erreur recalcul");
    } finally {
      setRecalculating(false);
      setLoading(false);
    }
  }

// Charger les options (essences, couleurs, coupes)
  useEffect(() => {
    let cancelled = false;
    async function loadOptions() {
      try {
        const res = await apiFetch("/api/options");
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setOptions({
            essences: data.essences || [],
            couleurs: data.couleurs || [],
            coupes: data.coupes || [],
          });
        }
      } catch (e) {
        console.error("Erreur chargement options:", e);
      }
    }
    loadOptions();
    return () => {
      cancelled = true;
    };
  }, []);


  const etats = useMemo(
    () => Array.from(new Set(rows.map((r) => r.etat).filter(Boolean))),
    [rows],
  );
  const fournisseurs = useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => r.fournisseur).filter(Boolean)),
      ).sort(),
    [rows],
  );

  const view = useMemo(() => {
    let out = [...rows];
    out = applyFilter(out, filterKey, filterValue);
    out = applyFilter(out, filterKey2, filterValue2);
    if (onlyRestantPositif) {
      out = out.filter((r) => {
        const n = Number(r.nombrePlacageRestant);
        return Number.isFinite(n) && n > 0;
      });
    }
    const dir = sortDir === "asc" ? 1 : -1;
    out.sort((a, b) => compareByKey(a, b, sortKey) * dir);
    return out;
  }, [
    rows,
    filterKey,
    filterValue,
    filterKey2,
    filterValue2,
    onlyRestantPositif,
    sortKey,
    sortDir,
  ]);

  // Calculs pagination
  const totalPages = Math.ceil(view.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedView = view.slice(startIndex, endIndex);

  // Réinitialiser à la page 1 quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [filterKey, filterValue, filterKey2, filterValue2, onlyRestantPositif, sortKey, sortDir]);


  // --- Fonctions de sélection multiple ---

  // Toggle une ligne spécifique
  const toggleRow = (pn) => {
    setSelectedPNs((prev) => {
      const next = new Set(prev);
      if (next.has(pn)) {
        next.delete(pn);
      } else {
        next.add(pn);
      }
      return next;
    });
  };

  // Sélectionner/désélectionner toute la page actuelle
  const toggleAllOnPage = () => {
    const allSelected = paginatedView.every((r) => selectedPNs.has(r.raw.PN));

    setSelectedPNs((prev) => {
      const next = new Set(prev);
      paginatedView.forEach((r) => {
        if (allSelected) {
          next.delete(r.raw.PN);
        } else {
          next.add(r.raw.PN);
        }
      });
      return next;
    });
  };

  // Tout désélectionner
  const clearSelection = () => {
    setSelectedPNs(new Set());
  };

  // Vérifier si toute la page est sélectionnée
  const isAllPageSelected = paginatedView.length > 0 &&
    paginatedView.every((r) => selectedPNs.has(r.raw.PN));

  const handleEdit = (rawItem) => {
    localStorage.setItem("woodoo_edit_item", JSON.stringify(rawItem));
    navigate("/logistique/reception/bois", {
      state: { item: rawItem, edit: true },
    });
  };

  const handleTrackingSaved = (pn, updates) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.raw?.PN !== pn) return r;
        const nextRaw = {
          ...r.raw,
          Date_en_cours: updates.dateEnCours || "",
          Date_fini: updates.dateFini || "",
          NombreDePlacageRestant: updates.placageRestant,
          Statut: updates.etat ?? r.raw.Statut,
        };
        return {
          ...r,
          dateEnCours: formatDate(updates.dateEnCours),
          dateFini: formatDate(updates.dateFini),
          nombrePlacageRestant: updates.placageRestant,
          etat: updates.etat ?? r.etat,
          prixTotale: calcPrixTotal(nextRaw),
          raw: nextRaw,
        };
      }),
    );
  };

  const openCertifPdf = async (key) => {
    if (!key) return;
    try {
      const r = await apiFetch(
        `/api/logistique/bois/download-certif?key=${encodeURIComponent(key)}`,
      );
      if (!r.ok) throw new Error("Impossible d'ouvrir le PDF");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(e.message || "Erreur ouverture PDF");
    }
  };

  const handleCertifPdfReplace = async (rawItem, file) => {
    if (!file || !rawItem?.PN) return;
    if (file.type !== "application/pdf") {
      alert("Fichier PDF uniquement");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert("PDF max 10 Mo");
      return;
    }
    setPdfBusyPn(rawItem.PN);
    try {
      const fd = new FormData();
      fd.append("pdf", file);
      fd.append("pn", rawItem.PN);
      const upRes = await apiFetch("/api/logistique/bois/upload-certif", {
        method: "POST",
        body: fd,
      });
      const up = await parseJsonResponse(upRes);
      if (!upRes.ok) throw new Error(up.message || "Erreur upload");

      const metaRes = await apiFetch("/api/logistique/bois/certif-pdf-meta", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          PN: rawItem.PN,
          "Num de lot": rawItem["Num de lot"],
          CertifPdfKey: up.certif_pdf_key,
          CertifPdfName: up.certif_pdf_name || file.name,
        }),
      });
      const meta = await parseJsonResponse(metaRes);
      if (!metaRes.ok) throw new Error(meta.message || "Erreur enregistrement");

      setRows((prev) =>
        prev.map((row) =>
          row.raw.PN === rawItem.PN
            ? {
                ...row,
                certifPdfKey: up.certif_pdf_key,
                certifPdfName: up.certif_pdf_name || file.name,
                raw: {
                  ...row.raw,
                  CertifPdfKey: up.certif_pdf_key,
                  CertifPdfName: up.certif_pdf_name || file.name,
                },
              }
            : row,
        ),
      );
    } catch (e) {
      alert(e.message || "Erreur remplacement PDF");
    } finally {
      setPdfBusyPn("");
    }
  };

   // --- Impression groupée avec DEBUG ---

// Helper : récupérer le nom depuis un code
const nameFromCode = (list = [], code) => {
  const it = list.find(
    (x) => String(x.code).toUpperCase() === String(code || "").toUpperCase()
  );
  return it ? it.nom : code;
};

// Fonction principale d'impression groupée
const handlePrintSelected = async () => {
  console.log("=== 🚀 DÉBUT IMPRESSION BATCH ===");
  console.log("selectedPNs (Set):", selectedPNs);
  console.log("selectedPNs.size:", selectedPNs.size);
  console.log("selectedPNs (Array):", Array.from(selectedPNs));

  if (selectedPNs.size === 0) {
    alert("Aucune ligne sélectionnée");
    return;
  }

  if (selectedPNs.size > 30) {
    alert("Maximum 30 lots par impression");
    return;
  }

  const confirmed = window.confirm(
    `Vous allez imprimer ${selectedPNs.size * 2} étiquettes ` +
    `(${selectedPNs.size} lots × 2 étiquettes).\n\nContinuer ?`
  );

  if (!confirmed) {
    console.log("❌ Impression annulée par l'utilisateur");
    return;
  }

  try {
    setPrinting(true);

    console.log("--- 📊 DONNÉES DISPONIBLES ---");
    console.log("Nombre total de rows:", rows.length);
    console.log("Options chargées:", options);

    // Récupérer les données complètes des lignes sélectionnées
    const selectedRows = rows.filter((r) => {
      const hasPN = selectedPNs.has(r.raw.PN);
      console.log(`  Ligne PN="${r.raw.PN}" sélectionnée:`, hasPN);
      return hasPN;
    });

    console.log("--- ✅ LIGNES FILTRÉES ---");
    console.log("Nombre de lignes sélectionnées:", selectedRows.length);
    console.log("Lignes complètes:", selectedRows);

    if (selectedRows.length === 0) {
      console.error("❌ ERREUR: Aucune ligne trouvée malgré la sélection !");
      console.log("rows complet:", rows);
      alert("Erreur: Aucune donnée trouvée pour les lignes sélectionnées");
      setPrinting(false);
      return;
    }

    // Construire le payload
    const printData = selectedRows.map((row, index) => {
      console.log(`--- 🏗️ CONSTRUCTION ITEM ${index + 1} ---`);
      console.log("  row.raw:", row.raw);
      console.log("  Essence code:", row.raw.Essence);
      console.log("  Couleur code:", row.raw.Couleur);
      console.log("  Coupe code:", row.raw.Coupe);

      const essence = nameFromCode(options.essences, row.raw.Essence);
      const couleur = nameFromCode(options.couleurs, row.raw.Couleur);
      const coupe = nameFromCode(options.coupes, row.raw.Coupe);

      console.log("  Essence nom:", essence);
      console.log("  Couleur nom:", couleur);
      console.log("  Coupe nom:", coupe);

      const detail = [
        essence,
        couleur,
        row.raw.Epaisseur ? `${row.raw.Epaisseur}mm` : "",
        coupe,
      ]
        .filter(Boolean)
        .join(" ");

      console.log("  Detail construit:", detail);

      const item = {
        pn: row.raw.PN,
        detail,
        date: row.raw.Date,
        nombreDePlacage: row.raw.NombreDePlacage,
        n: 2,
      };

      console.log("  ✅ Item final:", item);
      return item;
    });

    console.log("--- 📦 PAYLOAD FINAL ---");
    console.log("Nombre d'items:", printData.length);
    console.log("Payload complet:", printData);
    console.log("JSON stringifié:", JSON.stringify(printData, null, 2));

    // Appel API
    console.log("--- 🌐 APPEL API ---");
    await clientPrintBatchFromApi(printData);

    // Désélectionner tout après impression réussie
    console.log("--- ✅ IMPRESSION RÉUSSIE ---");
    clearSelection();
    console.log("Sélection réinitialisée");

    alert(`✅ ${selectedRows.length} lot(s) envoyé(s) à l'impression`);
  } catch (e) {
    console.error("--- ❌ ERREUR IMPRESSION ---");
    console.error("Message:", e.message);
    console.error("Stack:", e.stack);
    alert(`❌ Erreur : ${e.message}`);
  } finally {
    setPrinting(false);
    console.log("=== 🏁 FIN IMPRESSION BATCH ===\n\n");
  }
};

// Fonction d'impression batch (ouvre fenêtre popup)
// Fonction d'impression batch - Télécharge directement le PDF
async function clientPrintBatchFromApi(items) {
  const res = await apiFetch("/api/labels/batch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items, format: "PDF" }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `HTTP ${res.status}`);
  }

  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const fileName = `QR_Batch_${items.length}_lots.pdf`;

  // Téléchargement automatique
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();

  // Nettoyage
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  }, 100);
}





  // Générer les numéros de page à afficher
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <div style={styles.page}>
      <MenuBar active="logistique" />

      <div style={styles.wrap} className="wrap">
        <section style={styles.panel} className="card">
          <div style={styles.panelTitle}>SUIVIE DE STOCK</div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>{ui("LOT NEUF")}</div>

            <div style={styles.toolbar} className="toolbar">
              <div style={styles.group} className="group">
                <span style={styles.label}>{ui("Trier par :")}</span>
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  style={styles.select}
                >
                  <option value="index">Index</option>
                  <option value="numLot">Num lot Woodoo</option>
                  <option value="etat">État</option>
                  <option value="fournisseur">Fournisseur</option>
                  <option value="dateReception">Date de réception</option>
                  <option value="quantiteRestante">Nb placage restant</option>
                  <option value="nombreDePlacage">Nb placage</option>
                  <option value="prixTotale">Prix totales (€)</option>
                </select>
                <button
                  type="button"
                  onClick={() =>
                    setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                  }
                  style={styles.button}
                >
                  {sortDir === "asc" ? "↗︎ Asc" : "↘︎ Desc"}
                </button>
              </div>

              <div style={styles.group}>
                <span style={styles.label}>{ui("Filtrer par :")}</span>
                <select
                  value={filterKey}
                  onChange={(e) => {
                    setFilterKey(e.target.value);
                    setFilterValue("");
                  }}
                  style={styles.select}
                >
                  <option value="">{ui("— colonne —")}</option>
                  <option value="numLot">Num lot Woodoo</option>
                  <option value="etat">État</option>
                  <option value="fournisseur">Fournisseur</option>
                  <option value="dateReception">Date de réception</option>
                  <option value="quantiteKO">Quantité NOK</option>
                  <option value="quantiteRestante">Nb placage restant</option>
                  <option value="nombreDePlacage">Nb placage</option>
                  <option value="prixTotale">Prix totales (€)</option>
                </select>

                {filterKey === "etat" ? (
                  <select
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">{ui("— valeur —")}</option>
                    {etats.map((e) => (
                      <option key={e} value={e}>
                        {e}
                      </option>
                    ))}
                  </select>
                ) : filterKey === "fournisseur" ? (
                  <select
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">{ui("— valeur —")}</option>
                    {fournisseurs.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    placeholder="valeur…"
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                    style={styles.input}
                  />
                )}

                <div style={styles.group}>
                  <span style={styles.label}>Et :</span>
                  <select
                    value={filterKey2}
                    onChange={(e) => {
                      setFilterKey2(e.target.value);
                      setFilterValue2("");
                    }}
                    style={styles.select}
                  >
                    <option value="">{ui("— colonne —")}</option>
                    <option value="numLot">Num lot Woodoo</option>
                    <option value="etat">État</option>
                    <option value="fournisseur">Fournisseur</option>
                    <option value="dateReception">Date de réception</option>
                    <option value="quantiteRestante">Nb placage restant</option>
                  <option value="nombreDePlacage">Nb placage</option>
                    <option value="prixTotale">Prix totales (€)</option>
                  </select>

                  {filterKey2 === "etat" ? (
                    <select
                      value={filterValue2}
                      onChange={(e) => setFilterValue2(e.target.value)}
                      style={styles.select}
                    >
                      <option value="">{ui("— valeur —")}</option>
                      {etats.map((e) => (
                        <option key={e} value={e}>
                          {e}
                        </option>
                      ))}
                    </select>
                  ) : filterKey2 === "fournisseur" ? (
                    <select
                      value={filterValue2}
                      onChange={(e) => setFilterValue2(e.target.value)}
                      style={styles.select}
                    >
                      <option value="">{ui("— valeur —")}</option>
                      {fournisseurs.map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      placeholder="valeur…"
                      value={filterValue2}
                      onChange={(e) => setFilterValue2(e.target.value)}
                      style={styles.input}
                    />
                  )}
                </div>

               <button
  type="button"
  onClick={() => {
    if (isSuiviBois) {
      setFilterKey("fournisseur");
      setFilterValue(DEFAULT_BOIS_FOURNISSEUR);
      setOnlyRestantPositif(true);
    } else {
      setFilterKey("");
      setFilterValue("");
      setOnlyRestantPositif(false);
    }
    setFilterKey2("");
    setFilterValue2("");
    setSortKey("index");
    setSortDir("asc");
  }}
  style={{
    ...styles.button,
    background: "#fff",
    color: "#000",
  }}
>
  {ui("Réinitialiser")}
</button>

              {isSuiviBois && (
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#000",
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={onlyRestantPositif}
                  onChange={(e) => setOnlyRestantPositif(e.target.checked)}
                  style={{ width: 16, height: 16, cursor: "pointer" }}
                />
                Placage restant &gt; 0
              </label>
              )}

{/* ✅✅✅ NOUVEAU : Boutons de sélection ✅✅✅ */}
{selectedPNs.size > 0 && (
  <>
    <button
      type="button"
      onClick={handlePrintSelected}
      disabled={printing}
      style={{
        ...styles.button,
        background: "#E6FF3E",
        fontWeight: 800,
      }}
    >
      {printing
        ? "IMPRESSION..."
        : `IMPRIMER ${selectedPNs.size} SÉLECTION(S)`}
    </button>
    <button
      type="button"
      onClick={clearSelection}
      style={{
        ...styles.button,
        background: "#fff",
        color: "#000",
      }}
    >
      Tout désélectionner
    </button>
  </>
)}
{/* ✅✅✅ FIN DU NOUVEAU BLOC ✅✅✅ */}

<button
  type="button"
  onClick={handleRecalcStock}
  disabled={recalculating}
  style={{ ...styles.button }}
  title="Recalculer stock depuis les prélèvements enregistrés"
>
  {recalculating ? "Recalcul…" : "Recalculer stock"}
</button>

<button
  type="button"
  onClick={() => handleExportCsv(view)}
  style={{ ...styles.button }}
  title="Exporter la vue filtrée/triée en CSV"
>
  {ui("Export CSV")}
</button>

              </div>
            </div>

            {recalcMsg && (
              <div style={styles.recalcMsg}>{recalcMsg}</div>
            )}

            {/* Info pagination */}
            <div style={styles.paginationInfo}>
              <span style={styles.infoText}>
                Affichage {view.length === 0 ? 0 : startIndex + 1} à{" "}
                {Math.min(endIndex, view.length)} sur {view.length} résultats
              </span>
              <div style={styles.group}>
                <span style={styles.label}>Lignes par page :</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={styles.select}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            <div style={{ overflowX: "auto" }} className="tableWrap">
              <table style={styles.table}>
                <thead>
                  <tr>
                      {/* ✅✅✅ NOUVELLE COLONNE : Checkbox header ✅✅✅ */}
      <Th style={{ width: 50 }}>
        <input
          type="checkbox"
          checked={isAllPageSelected}
          onChange={toggleAllOnPage}
          style={{ cursor: "pointer", width: 18, height: 18 }}
          title="Sélectionner/désélectionner toute la page"
        />
      </Th>
                    <Th style={{ width: 100 }}>{ui("INDEX")}</Th>
                    <Th style={{ width: 360 }}>{ui("NUM LOT WOODOO")}</Th>
                    <Th style={{ width: 140 }}>{ui("FOURNISSEUR")}</Th>
                    <Th style={{ width: 120 }}>{ui("ETAT")}</Th>
                    <Th style={{ width: 170 }}>{ui("DATE DE RÉCEPTION")}</Th>
                    <Th style={{ width: 160 }}>{ui("DATE EN COURS")}</Th>
                    <Th style={{ width: 140 }}>{ui("DATE FINI")}</Th>
                    <Th style={{ width: 140 }}>{ui("QUANTITÉ NOK")}</Th>
                    <Th style={{ width: 130 }}>{ui("NB PLACAGE")}</Th>
                    <Th style={{ width: 150 }}>{ui("NB PLACAGE RESTANT")}</Th>
                    <Th style={{ width: 140 }}>{ui("PRIX TOTALES (€)")}</Th>
                    <Th style={{ width: 90 }}>{ui("CERTIF")}</Th>
                    <Th style={{ width: 180 }}>{ui("CERTIFICAT PDF")}</Th>
                    <Th style={{ width: 160 }}>{ui("ACTIONS")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={15} style={styles.loadingCell}>
                       Aucune Ligne
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    paginatedView.map((r, idx) => (
                      <TrackingFields
                        key={`${r.numLot}-${idx}`}
                        pn={r.raw?.PN}
                        apiPath="/api/bois/tracking"
                        dateEnCours={r.raw?.Date_en_cours}
                        dateFini={r.raw?.Date_fini}
                        placageRestant={r.nombrePlacageRestant}
                        statut={r.etat || r.raw?.Statut}
                        onSaved={(updates) => handleTrackingSaved(r.raw.PN, updates)}
                      >
                      <tr
                       style={{
    background: selectedPNs.has(r.raw.PN) ? "#E6FF3E20" : "#fff",
  }}
                      >

                            {/* ✅✅✅ NOUVELLE CELLULE : Checkbox ligne ✅✅✅ */}
        <Td center>
          <input
            type="checkbox"
            checked={selectedPNs.has(r.raw.PN)}
            onChange={() => toggleRow(r.raw.PN)}
            style={{ cursor: "pointer", width: 18, height: 18 }}
          />
        </Td>
                        <Td center>{r.index}</Td>
                        <Td>{r.numLot || ""}</Td>
                        <Td center>{r.fournisseur}</Td>
                        <Td center>
                          <TrackingStatutSelect />
                          <TrackingSaveButton />
                        </Td>
                        <Td center>{r.dateReception || ""}</Td>
                        <Td center>
                          <TrackingDateEnCoursInput />
                        </Td>
                        <Td center>
                          <TrackingDateFiniInput />
                        </Td>
                        <Td center>{r.quantiteKO}</Td>
                        <Td center>{r.nombreDePlacage}</Td>
                        <Td center>
                          <TrackingPlacageRestantInput />
                        </Td>
                        <Td center>{r.prixTotale}</Td>
                        <Td center>{r.certif || "—"}</Td>
                        <Td center>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 4,
                              alignItems: "center",
                            }}
                          >
                            {r.certifPdfKey ? (
                              <button
                                type="button"
                                style={{
                                  ...styles.actionBtn,
                                  height: 28,
                                  fontSize: 12,
                                }}
                                onClick={() => openCertifPdf(r.certifPdfKey)}
                                title={r.certifPdfName || "Voir le certificat"}
                              >
                                {ui("VOIR PDF")}
                              </button>
                            ) : (
                              <span style={{ color: "#666", fontSize: 12 }}>
                                Aucun
                              </span>
                            )}
                            <label
                              style={{
                                cursor:
                                  pdfBusyPn === r.raw.PN ? "wait" : "pointer",
                                width: "100%",
                              }}
                            >
                              <input
                                type="file"
                                accept="application/pdf"
                                style={{ display: "none" }}
                                disabled={pdfBusyPn === r.raw.PN}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) handleCertifPdfReplace(r.raw, f);
                                  e.target.value = "";
                                }}
                              />
                              <span
                                style={{
                                  ...styles.actionBtn,
                                  display: "inline-block",
                                  height: 28,
                                  fontSize: 11,
                                  lineHeight: "28px",
                                  opacity: pdfBusyPn === r.raw.PN ? 0.6 : 1,
                                }}
                              >
                                {pdfBusyPn === r.raw.PN
                                  ? "..."
                                  : r.certifPdfKey
                                    ? "REMPLACER"
                                    : "AJOUTER"}
                              </span>
                            </label>
                          </div>
                        </Td>
                        <Td center>
                          <button
                            style={styles.actionBtn}
                            onClick={() => handleEdit(r.raw)}
                            title="Modifier ce lot"
                          >
                            MODIFIER
                          </button>
                        </Td>
                      </tr>
                      </TrackingFields>
                    ))}

                  {!loading && view.length === 0 && (
                    <tr>
                      <td colSpan={15} style={styles.loadingCell}>
                        {ui("Aucune ligne")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Contrôles pagination */}
            {!loading && view.length > 0 && (
              <div style={styles.paginationControls} className="pagination">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  style={{
                    ...styles.pageBtn,
                    ...(currentPage === 1 ? styles.pageBtnDisabled : {}),
                  }}
                  title="Première page"
                >
                  ««
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    ...styles.pageBtn,
                    ...(currentPage === 1 ? styles.pageBtnDisabled : {}),
                  }}
                  title="Page précédente"
                >
                  «
                </button>

                {getPageNumbers().map((page, idx) =>
                  page === "..." ? (
                    <span key={`ellipsis-${idx}`} style={styles.ellipsis}>
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        ...styles.pageBtn,
                        ...(currentPage === page ? styles.pageBtnActive : {}),
                      }}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    ...styles.pageBtn,
                    ...(currentPage === totalPages
                      ? styles.pageBtnDisabled
                      : {}),
                  }}
                  title="Page suivante"
                >
                  »
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  style={{
                    ...styles.pageBtn,
                    ...(currentPage === totalPages
                      ? styles.pageBtnDisabled
                      : {}),
                  }}
                  title="Dernière page"
                >
                  »»
                </button>
              </div>
            )}

            {err && <div style={styles.error}>⚠ {err}</div>}
          </div>
        </section>
      </div>
    </div>
  );
}

/* ===== Helpers ===== */
function formatDate(d) {
  if (!d) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  }
  return d;
}
function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : "";
}
function parseDateDDMMYYYY(s) {
  if (!s || typeof s !== "string") return 0;
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return 0;
  const [_, dd, mm, yyyy] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  return d.getTime();
}
function compareByKey(a, b, key) {
  const va = a[key],
    vb = b[key];
  if (key === "dateReception")
    return parseDateDDMMYYYY(va) - parseDateDDMMYYYY(vb);
  if (
    key === "index" ||
    key === "quantiteRestante" ||
    key === "nombrePlacageRestant" ||
    key === "nombreDePlacage" ||
    key === "quantiteKO" ||
    key === "prixTotale"
  )
    return (Number(va) || 0) - (Number(vb) || 0);
  if (key === "fournisseur")
    return String(va || "").localeCompare(String(vb || ""), "fr", {
      sensitivity: "base",
    });
  return String(va || "").localeCompare(String(vb || ""), "fr", {
    sensitivity: "base",
  });
}
function Th({ children, style }) {
  const { ui } = useI18n();
  const _c = typeof children === "string" ? ui(children) : children;

  return <th style={{ ...styles.th, ...(style || {}) }}>{_c}</th>;
}
function Td({ children, center, noPad }) {
  return (
    <td
      style={{
        ...styles.td,
        textAlign: center ? "center" : "left",
        padding: noPad ? 0 : styles.td.padding,
      }}
    >
      {children}
    </td>
  );
}

/* --- Styles --- */
const LIME = "#E6FF3E";
const styles = {
  page: {
    minHeight: "100vh",
    background: "#000",
    color: "#fff",
    fontFamily:
      'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  wrap: { padding: "16px" },
  panel: { background: "#63686f", border: "1px solid #3c4046", padding: 16 },
  panelTitle: {
    color: "#fff",
    fontWeight: 800,
    textTransform: "uppercase",
    marginBottom: 10,
  },
  card: { background: "#e9eaed", border: "1px solid #c9cbd1" },
  cardTitle: {
    background: "#d8d9db",
    color: "#000",
    fontWeight: 800,
    textTransform: "uppercase",
    padding: "10px 16px",
    borderBottom: "1px solid #c9cbd1",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 12px",
    borderBottom: "1px solid #c9cbd1",
    background: "#f3f4f6",
  },
  group: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  label: { color: "#000", fontWeight: 700, fontSize: 12 },
  select: {
    height: 34,
    border: "1px solid #c0c0c0",
    background: "#fff",
    padding: "0 8px",
  },
  input: {
    height: 34,
    border: "1px solid #c0c0c0",
    background: "#fff",
    padding: "0 8px",
  },
  button: {
    height: 34,
    padding: "0 10px",
    border: "none",
    background: LIME,
    color: "#000",
    fontWeight: 800,
    cursor: "pointer",
  },
  recalcMsg: {
    margin: "8px 12px 0",
    padding: "8px 12px",
    background: "#002810",
    border: "1px solid #00B050",
    borderRadius: 8,
    color: "#80ff90",
    fontSize: 13,
    fontWeight: 600,
  },
  paginationInfo: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 12px",
    background: "#f3f4f6",
    borderBottom: "1px solid #c9cbd1",
    flexWrap: "wrap",
    gap: 10,
  },
  infoText: {
    color: "#000",
    fontSize: 13,
    fontWeight: 600,
  },
  paginationControls: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 6,
    padding: "12px",
    background: "#f3f4f6",
    borderTop: "1px solid #c9cbd1",
    flexWrap: "wrap",
  },
  pageBtn: {
    minWidth: 36,
    height: 36,
    padding: "0 10px",
    border: "1px solid #c0c0c0",
    background: "#fff",
    color: "#000",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s",
  },
  pageBtnActive: {
    background: LIME,
    borderColor: LIME,
    fontWeight: 800,
  },
  pageBtnDisabled: {
    background: "#e0e0e0",
    color: "#999",
    cursor: "not-allowed",
    opacity: 0.6,
  },
  ellipsis: {
    padding: "0 8px",
    color: "#000",
    fontWeight: 700,
  },
  table: { width: "100%", borderCollapse: "collapse", background: "#fff" },
  th: {
    background: "#d9d9d9",
    color: "#000",
    fontWeight: 800,
    textTransform: "uppercase",
    border: "1px solid #b7b7b7",
    padding: "10px 6px",
  },
  td: {
    background: "#fff",
    color: "#000",
    border: "1px solid #c7c7c7",
    padding: "8px 6px",
    fontWeight: 500,
  },
  actionBtn: {
    background: LIME,
    color: "#000",
    fontWeight: 800,
    border: "none",
    width: "100%",
    height: 36,
    cursor: "pointer",
  },
  error: { marginTop: 10, color: "#ffb3b3", fontWeight: 600 },
  loadingCell: { padding: 16, textAlign: "center", background: "#fff" },
};

/* ✅ Responsive media queries */
const mediaStyle = document.createElement("style");
mediaStyle.innerHTML = `
/* --- Mobile --- */
@media (max-width: 639px) {
  .wrap { padding: 8px !important; }
  .toolbar { flex-direction: column !important; align-items: stretch !important; }
  .group { flex-direction: column !important; align-items: stretch !important; width: 100% !important; }
  .tableWrap { overflow-x: auto; }
  table { font-size: 13px !important; }
  th, td { padding: 8px 4px !important; }
  .actionBtn { font-size: 13px !important; height: 40px !important; }
  .pagination { gap: 4px !important; padding: 8px !important; }
  .pagination button { min-width: 32px !important; height: 32px !important; font-size: 13px !important; }
}

/* --- Tablette --- */
@media (min-width: 640px) and (max-width: 1023px) {
  .wrap { padding: 12px 24px !important; }
  .toolbar { flex-wrap: wrap !important; gap: 8px !important; }
  .pagination { gap: 5px !important; }
}

/* --- Desktop --- */
@media (min-width: 1024px) {
  .wrap { padding: 24px 64px !important; }
  .toolbar { gap: 12px !important; }
  th, td { padding: 10px 8px !important; }
}
`;
document.head.appendChild(mediaStyle);
